// Turns REPORT_SPEC + the Cash Flow Audit payload into one ordered array of rows.
//
// Pure: no React, no DOM, no Handsontable — unit-testable under bare node. The
// component derives `data`, `cells()`, `rowHeights()` and click handling from the
// single array this produces, so the row model and the styling model cannot
// desync: there is only one array.

import { REPORT_SPEC } from './reportSpec.js'
import { allZeroDisplayed, formatMonthYear, formatMoneyWhole } from '../utils/reportFormat.js'

// 1. EXPAND — resolve each spec entry against the payload, exploding `repeat`
// entries (OpEx / CapEx line items) into one row per item.
export function expand(rows, periodCount, labelOf) {
  const blank = Array.from({ length: periodCount }, () => '0')

  return REPORT_SPEC.flatMap((spec) => {
    if (spec.kind === 'section') {
      return [{ ...spec, label: labelOf(spec.labelKey), values: [] }]
    }
    if (spec.repeat) {
      return (rows[spec.repeat] || []).map((item) => ({
        ...spec,
        key: `${spec.key}:${item.id}`,
        label: item.label,
        values: item.values || blank
      }))
    }
    return [{ ...spec, label: labelOf(spec.labelKey), values: rows[spec.source] || blank }]
  })
}

// 2. SUPPRESS — spec §5, three declarative passes over the whole table. A row is
// shown or hidden across the full analysis period, never per year.
export function suppress(resolved) {
  // Pass 1: the plain §5 test. Anchors are exempt; section headings and section
  // totals defer to pass 3.
  const kept = resolved.filter((row) => {
    if (row.kind === 'section') return true
    if (row.suppressWith === 'section') return true
    return row.always === true || !allZeroDisplayed(row.values)
  })

  // Pass 2: a section is live when any of its own rows survived. A section total
  // votes only when it is itself non-zero — §4 wants the total to disappear with
  // its section when the items cancel out, but §5 forbids hiding a row that is
  // not zero (three items of -0.4 each round to zero individually while their
  // total rounds to -1, and hiding that total would break the on-screen sum).
  const liveSections = new Set(
    kept
      .filter((row) => row.kind !== 'section' && row.section &&
                       (row.suppressWith !== 'section' || !allZeroDisplayed(row.values)))
      .map((row) => row.section)
  )

  // Pass 3: drop dead sections and the totals that belong to them. `always`
  // sections (Rental Revenue, §7.1 — the one heading with no "when applicable"
  // qualifier) survive even when every optional row beneath them is zero.
  return kept.filter((row) => {
    if (row.kind === 'section') return row.always === true || liveSections.has(row.section)
    if (row.suppressWith === 'section') return liveSections.has(row.section)
    return true
  })
}

// 3. TENANTS — spec §6. Children are spliced in directly beneath their parent,
// which keeps showing its complete total.
export function withTenants(visible, rows, expandedKeys, detailed) {
  return visible.flatMap((row) => {
    // Filter first, then decide on the caret: the server drops tenant rows only on
    // EXACT zero, so a tenant sitting at 0.4 in every period arrives in the payload
    // but must not be offered as an expansion that opens onto nothing.
    const children = (rows[row.tenantSource] || []).filter((child) => !allZeroDisplayed(child.values || []))
    // With detailed=false the tenant arrays are absent entirely, so expandability
    // has to come from the spec — otherwise the user could never trigger the fetch
    // that produces them.
    const expandable = Boolean(row.tenantSource) && (!detailed || children.length > 0)
    const expanded = expandable && expandedKeys.has(row.key)
    const self = { ...row, depth: row.kind === 'minor' ? 1 : 0, expandable, expanded }

    if (!expanded) return [self]

    return [self, ...children.map((child) => ({
      // child.id is opaque: credit_loss_tenants can carry the property-level
      // pseudo-row ('property-level-opex-recoveries'). Never parse it as an id.
      key: `${row.key}:${String(child.id)}`,
      kind: 'tenant',
      depth: 2,
      label: child.title,
      values: child.values || [],
      rule: 'none',
      bold: false,
      expandable: false,
      expanded: false
    }))]
  })
}

// Spec §2: a two-level header — Year, then Period End as MMM-YYYY — for every
// frequency (unlike the Audit tab, which adds Quarter and Month rows in the
// finer views). The year label is printed once per year group and left blank for
// the remaining columns, so Monthly does not repeat "Year 1" twelve times.
const COLUMNS_PER_YEAR = { monthly: 12, quarterly: 4, annual: 1 }

export function buildHeaderRows(periodEnds, view, labelOf) {
  const perYear = COLUMNS_PER_YEAR[view] || 1
  const yearCells = periodEnds.map((_, index) => (
    index % perYear === 0 ? labelOf('headers.year_n', { n: Math.floor(index / perYear) + 1 }) : ''
  ))

  return [
    { key: '__year', kind: 'header_year', depth: 0, rule: 'none',
      cells: [labelOf('headers.year'), ...yearCells] },
    { key: '__period', kind: 'header_period', depth: 0, rule: 'none',
      cells: [labelOf('headers.period_end'), ...periodEnds.map(formatMonthYear)] }
  ]
}

// THE spine. Index 0 and 1 are the header rows, so gridRows[r] is a direct lookup
// with no offset arithmetic anywhere — this is what replaces the row-index
// bookkeeping the Audit tab carries.
export function buildGridRows({ rows, periodEnds, view, expandedKeys, detailed, labelOf }) {
  const periodCount = periodEnds.length
  if (periodCount === 0) return []

  const body = withTenants(
    suppress(expand(rows, periodCount, labelOf)),
    rows,
    expandedKeys,
    detailed
  ).map((row) => ({
    ...row,
    cells: row.kind === 'section'
      // §3: section headings carry no monetary values.
      ? [row.label, ...Array.from({ length: periodCount }, () => '')]
      : [row.label, ...row.values.map(formatMoneyWhole)]
  }))

  return [...buildHeaderRows(periodEnds, view, labelOf), ...body]
}
